import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FileUpload, SdirCheckbox, SdirModal } from '@sdir/sds';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { generatePath, useLocation } from 'react-router-dom';
import {
  AttachmentResult,
  AttachmentType,
  GetApplicationsQueryResult,
  SimulatorResultItem,
} from '@sdir/httpclient/lib/clients/aps/application';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { ErrorWithTrace } from '@sdir/blueprint.aps/lib/components';
import { AttachmentsButton, InputError } from '../../Atoms';
import { AddedQualifications, QualificationType } from '../../../types';
import {
  QUALIFICATION_ATTACHMENTTYPE,
  QUALIFICATION_URI,
  attachmentIsValid,
  qualificationHasAnyAddedInfo,
  qualificationHasValidAddedInfo,
  setInStorage,
} from '../../../helpers/Qualifications';
import { getBehaviourForQualification } from '../../../helpers/addedInfoHelpers';
import {
  buildCreateAttachmentGroupSequence,
  CreateAttachmentGroupTracker,
} from '../../../helpers/attachmentApiHelpers';
import {
  useCurrentApplication,
  useCurrentPerson,
  useFeatureToggles,
  useSubmitSequence,
} from '../../../services/hooks';
import { SubmitSequenceSummary } from '../SubmitSequenceSummary';
import { routes } from '../../../constants/routes';
import { ToggleDisableNewSeagoing, isToggleOn } from '../../../constants/permissions';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';
import { confirmationApi } from '../../../httpclient';

interface QualificationActionsProps {
  qualification: SimulatorResultItem;
  addedQualifications: AddedQualifications;
  qualificationStatus?: string;
  qualificationKey: string;
  isExternalUser?: boolean;
}

export const QualificationActions: FC<QualificationActionsProps> = ({
  qualification,
  addedQualifications,
  qualificationStatus,
  qualificationKey,
  isExternalUser = false,
}) => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const behaviour = useMemo(() => {
    return getBehaviourForQualification(qualification, isExternalUser);
  }, [qualification]);
  const { application, refresh } = useCurrentApplication();
  const { pathname } = useLocation();
  const onMissingDocPage = pathname.includes('/uploadmissingdocumentation');
  const featureToggle = useFeatureToggles();
  const [confirmed, setConfirmed] = useState<boolean>(false);

  const {
    errorObject: confirmError,
    loading: confirmLoading,
    callApi: submitConfirmation,
  } = useApi({
    apiFunction: (p) =>
      confirmationApi.v1QualificationconfirmationConfirmqualificationBodyPut(p.uid, p.uri, {
        confirmedText: p.text,
      }),
    successCallback: () => {
      refresh?.();
    },
  });

  const {
    errorObject: removeConfirmError,
    loading: removeConfirmLoading,
    callApi: removeConfirmation,
  } = useApi({
    apiFunction: (p) =>
      confirmationApi.v1QualificationconfirmationRemovequalificationconfirmationUriDelete(
        p.uid,
        p.uri
      ),
    successCallback: () => {
      refresh?.();
    },
  });

  const {
    person: { loading, result: currentPerson },
  } = useCurrentPerson();
  const enableNewSeagoing = !isToggleOn(
    featureToggle?.result,
    ToggleDisableNewSeagoing,
    currentPerson?.personalIdentificationNumber || ''
  );

  const addedAttachmentsForQualification = useMemo(() => {
    return (
      application.result?.attachments?.filter(
        (a) => a.simulatorResultItemUri === qualification.uri
      ) || []
    );
  }, [application, qualification]);

  const { hasAnyNewSeagoing, hasValidNewSeagoing } = useMemo(() => {
    if (!enableNewSeagoing || qualification?.type !== QualificationType.SeagoingTime) {
      return { hasAnyNewSeagoing: false, hasValidNewSeagoing: false };
    }
    return {
      hasAnyNewSeagoing: qualificationHasAnyAddedInfo(qualification, addedQualifications, []),
      hasValidNewSeagoing: qualificationHasValidAddedInfo(qualification, addedQualifications, []),
    };
  }, [enableNewSeagoing, qualification, addedQualifications]);

  const hasValidAddedInfo = useMemo(() => {
    if (!addedAttachmentsForQualification?.length) return false;
    const attachment = addedAttachmentsForQualification[0];
    return !!attachment && attachmentIsValid(attachment, addedQualifications, true);
  }, [addedAttachmentsForQualification, addedQualifications]);

  const goToGroup = useCallback(
    (group: string, uri?: string, attachmentType?: AttachmentType) => {
      if (!behaviour.canAddInfo || !application.result?.uid || !behaviour.editRoute) return;
      setInStorage(QUALIFICATION_URI, uri || '');
      setInStorage(QUALIFICATION_ATTACHMENTTYPE, attachmentType || '');
      navigate({
        pathname: generatePath(behaviour.editRoute, {
          applicationUid: application.result.uid,
          attachmentGroup: group,
        }),
        search: onMissingDocPage ? `?from=missingdocumentation` : '',
      });
    },
    [application.result, behaviour, navigate, onMissingDocPage]
  );

  const handleAttachmentGroupCreated = useCallback(
    (groupUid: string) => {
      refresh?.();
      goToGroup(groupUid);
    },
    [goToGroup, refresh]
  );

  const onChangeConfirmation = (value: boolean) => {
    setConfirmed(value);
    const escapedUri = encodeURIComponent(qualification?.uri || '');
    if (value) {
      submitConfirmation({
        uid: application?.result?.uid,
        uri: escapedUri,
        text: qualification.description,
      });
    } else {
      removeConfirmation({ uid: application?.result?.uid, uri: escapedUri });
    }
  };

  const openSeagoing = useCallback(() => {
    navigate({
      pathname: generatePath(routes.application.addSeagoingNew, {
        applicationUid: application?.result?.uid || '',
        requirement: qualificationKey,
      }),
      search: onMissingDocPage ? `?from=missingdocumentation` : '',
    });
  }, [application.result, navigate, qualificationKey, onMissingDocPage]);

  useEffect(() => {
    const confirmList = application?.result?.confirmedQualifications || [];
    const currentValue = confirmList.find(
      (c) => c.qualificationUri?.toLowerCase() === qualification.uri?.toLowerCase()
    );
    if (currentValue) {
      setConfirmed(true);
    }
  }, [application?.result, qualification?.uri]);

  if (
    qualification.type !== QualificationType.Declaration &&
    qualification.status &&
    !addedAttachmentsForQualification.length
  ) {
    return null;
  }

  const onEditSeagoing = enableNewSeagoing && !isExternalUser ? openSeagoing : undefined;
  const showEditButton = addedAttachmentsForQualification.length || hasValidNewSeagoing;

  return (
    <>
      {application.result && behaviour.canConfirm && (
        <ActionsWrapper data-testid={qualification?.title}>
          <SdirCheckbox
            checked={confirmed}
            disabled={confirmLoading || removeConfirmLoading}
            onClick={() => onChangeConfirmation(!confirmed)}
            title={intl.formatMessage({ id: 'qualification.confirm' })}
          />
        </ActionsWrapper>
      )}
      {application.result && behaviour.canAddInfo && (
        <ActionsWrapper data-testid={qualification?.title}>
          {showEditButton && (
            <>
              <EditInfoButton
                attachments={addedAttachmentsForQualification}
                onClick={goToGroup}
                onEditSeagoing={onEditSeagoing}
                className={qualificationStatus}
                attachmentType={behaviour.attachmentType}
              />
              {!hasValidAddedInfo && !hasValidNewSeagoing && (
                <InputError>
                  {intl.formatMessage({ id: 'qualification.errors.infoincomplete' })}
                </InputError>
              )}
            </>
          )}
          {!showEditButton && (
            <AddInfoButton
              application={application.result}
              attachmentType={behaviour.attachmentType}
              labelId="qualification.doc.upload"
              simulatorResultItemUri={qualification.uri}
              onAttachmentGroupCreated={handleAttachmentGroupCreated}
              onAddRequirement={goToGroup}
              onAddSeagoing={onEditSeagoing}
            />
          )}
        </ActionsWrapper>
      )}
      {confirmError && (
        <ErrorWithTrace
          error={confirmError}
          trace={confirmError?.response?.headers?.traceresponse}
        />
      )}
      {removeConfirmError && (
        <ErrorWithTrace
          error={removeConfirmError}
          trace={removeConfirmError?.response?.headers?.traceresponse}
        />
      )}
    </>
  );
};

const ActionsWrapper = styled.div`
  margin-top: 16px;

  [data-testid='checkbox-wrapper'] {
    margin-left: 48px;
  }
`;

interface EditInfoButtonProps {
  attachments: AttachmentResult[];
  attachmentType?: AttachmentType;
  className?: string;
  onClick: (group: string) => void;
  onEditSeagoing?: () => void;
}

export const EditInfoButton: React.FC<EditInfoButtonProps> = ({
  attachments,
  attachmentType,
  className,
  onClick,
  onEditSeagoing,
}) => {
  const intl = useIntl();
  const attachmentGroup = useMemo(() => attachments[0]?.group, [attachments]);

  const handleClick = useCallback(() => {
    if (!attachmentGroup && !onEditSeagoing) return;

    if (attachmentType === AttachmentType.SeagoingTime && onEditSeagoing) {
      onEditSeagoing();
    } else {
      onClick(attachmentGroup || '');
    }
  }, [attachmentGroup, attachmentType, onClick, onEditSeagoing]);

  if (!attachmentGroup && !onEditSeagoing) return null;

  return (
    <AttachmentsButton
      onClick={handleClick}
      attachments={attachments}
      buttonText={intl.formatMessage({ id: 'qualification.doc.edit' })}
      showFileIcon={false}
      className={className}
    />
  );
};

interface AddInfoButtonProps {
  application: GetApplicationsQueryResult;
  attachmentType?: AttachmentType;
  labelId?: string | null;
  simulatorResultItemUri?: string | null;
  onAttachmentGroupCreated: (groupUid: string) => void;
  onAddRequirement: (group: string, uri: string, attachmentType: AttachmentType) => void;
  onAddSeagoing?: () => void;
}

export const AddInfoButton: React.FC<AddInfoButtonProps> = ({
  application,
  labelId,
  attachmentType,
  simulatorResultItemUri,
  onAttachmentGroupCreated,
  onAddRequirement,
  onAddSeagoing,
}) => {
  const intl = useIntl();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showSubmitResults, setShowSubmitResults] = useState(false);
  const [submitTracker, setSubmitTracker] = useState<CreateAttachmentGroupTracker>({});
  const { submit, isSubmitting, results } = useSubmitSequence(buildCreateAttachmentGroupSequence);
  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      if (!files.length || !application.uid || !attachmentType) return;
      setUploadProgress(0);
      const { tracker } = await submit(
        {
          application,
          attachmentType,
          files,
          simulatorResultItemUri,
          progressListener: setUploadProgress,
        },
        submitTracker
      );
      if (tracker.errors?.length) {
        setShowSubmitResults(true);
        setSubmitTracker(tracker);
      } else {
        const createdGroupUid =
          Object.values(tracker.addedAttachments || {})[0].result?.group || '';
        onAttachmentGroupCreated(createdGroupUid);
      }
    },
    [
      application,
      attachmentType,
      onAttachmentGroupCreated,
      simulatorResultItemUri,
      submit,
      submitTracker,
    ]
  );

  const hideErrorModal = useCallback(() => setShowSubmitResults(false), []);

  if (attachmentType === AttachmentType.SeagoingTime && onAddSeagoing) {
    return (
      <AddInfoButtonWrapper>
        <AttachmentsButton
          onClick={() => onAddSeagoing?.()}
          attachments={[]}
          buttonText={intl.formatMessage({
            id: labelId || 'qualification.doc.upload',
          })}
          showFileIcon={false}
          className="warning"
        />
      </AddInfoButtonWrapper>
    );
  }

  if (
    attachmentType === AttachmentType.AdditionalCompetence ||
    attachmentType === AttachmentType.Course ||
    attachmentType === AttachmentType.Education ||
    attachmentType === AttachmentType.Certificate ||
    attachmentType === AttachmentType.Assessment ||
    attachmentType === AttachmentType.Confirmation ||
    attachmentType === AttachmentType.HealthCheck ||
    attachmentType === AttachmentType.Photo ||
    attachmentType === AttachmentType.Signature ||
    attachmentType === AttachmentType.ForeignId ||
    attachmentType === AttachmentType.NorwegianPassportOrId ||
    attachmentType === AttachmentType.SeagoingTime
  ) {
    return (
      <AddInfoButtonWrapper>
        <AttachmentsButton
          onClick={() => onAddRequirement('new', simulatorResultItemUri || '', attachmentType)}
          attachments={[]}
          buttonText={intl.formatMessage({
            id: labelId || 'qualification.doc.upload',
          })}
          showFileIcon={false}
          className="warning"
        />
      </AddInfoButtonWrapper>
    );
  }

  return (
    <AddInfoButtonWrapper>
      <FileUpload
        allowedTypes=".png,.jpg,.jpeg,.pdf"
        buttonLabel={intl.formatMessage({
          id: labelId || 'qualification.doc.upload',
        })}
        multiple
        buttonClass="warning"
        uploadProgress={isSubmitting ? uploadProgress || 1 : undefined}
        onFilesSelected={handleFilesSelected}
      />
      {showSubmitResults && (
        <SdirModal
          visible
          cancelText={intl.formatMessage({ id: 'general.form.close' })}
          closeButton
          onCancel={hideErrorModal}
        >
          <SubmitSequenceSummary results={results} />
        </SdirModal>
      )}
    </AddInfoButtonWrapper>
  );
};

const AddInfoButtonWrapper = styled.div`
  button {
    justify-content: center;
    font-family: ${({ theme }) => theme.fonts.stfBook};
    svg {
      display: none;
    }
  }

  #file_upload button {
    color: #000000;
  }
`;
